import { useState } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Checkbox, Input, Radio, Select, Textarea } from '@/components/ui'
import { blankBolRequest, blankParty } from '../api/types'
import type { BolParty, BolRequest, CodTerms, FreightChargeTerms } from '../api/types'
import { useGenerateBol } from '../api/queries'
import { CommodityLinesEditor } from '../components/CommodityLinesEditor'
import { CustomerOrderLinesEditor } from '../components/CustomerOrderLinesEditor'

// ─── Section wrapper (matches warehouse-incidents' form archetype) ──

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="bg-[#1A3A5C] text-white text-sm font-bold px-3 py-2 rounded-t">
      {label}
    </div>
  )
}

function SectionBody({ children }: { children: React.ReactNode }) {
  return (
    <div className="border border-border-default border-t-0 rounded-b p-4 space-y-4">
      {children}
    </div>
  )
}

function FieldGroup({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">{children}</div>
}

function Field({
  label,
  children,
  colSpan = 1,
}: {
  label: string
  children: React.ReactNode
  colSpan?: 1 | 2 | 3
}) {
  const spanClass = colSpan === 3 ? 'sm:col-span-3' : colSpan === 2 ? 'sm:col-span-2' : ''
  return (
    <div className={`space-y-1 ${spanClass}`}>
      <label className="text-xs text-secondary uppercase tracking-wide font-medium">{label}</label>
      {children}
    </div>
  )
}

function PartyFields({
  party,
  onChange,
  referenceLabel,
}: {
  party: BolParty
  onChange: (party: BolParty) => void
  referenceLabel: string
}) {
  return (
    <FieldGroup>
      <Field label="Name" colSpan={2}>
        <Input value={party.name} onChange={(e) => onChange({ ...party, name: e.target.value })} />
      </Field>
      <Field label={referenceLabel}>
        <Input value={party.reference_id} onChange={(e) => onChange({ ...party, reference_id: e.target.value })} />
      </Field>
      <Field label="Address" colSpan={2}>
        <Input value={party.address} onChange={(e) => onChange({ ...party, address: e.target.value })} />
      </Field>
      <Field label="City / State / ZIP">
        <Input value={party.city_state_zip} onChange={(e) => onChange({ ...party, city_state_zip: e.target.value })} />
      </Field>
    </FieldGroup>
  )
}

const FREIGHT_TERMS_OPTIONS: { value: FreightChargeTerms; label: string }[] = [
  { value: 'prepaid', label: 'Prepaid' },
  { value: 'collect', label: 'Collect' },
  { value: '3rd_party', label: '3rd Party' },
]

const COD_TERMS_OPTIONS: { value: CodTerms; label: string }[] = [
  { value: '', label: 'N/A' },
  { value: 'collect', label: 'Collect' },
  { value: 'prepaid', label: 'Prepaid' },
]

export function BillOfLadingPage() {
  const [form, setForm] = useState<BolRequest>(blankBolRequest())
  const [thirdParty, setThirdParty] = useState(false)
  const generateBol = useGenerateBol()

  function set<K extends keyof BolRequest>(field: K, value: BolRequest[K]) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  async function handleGenerate() {
    try {
      const body: BolRequest = {
        ...form,
        third_party_bill_to: thirdParty ? form.third_party_bill_to ?? blankParty() : null,
      }
      const result = await generateBol.mutateAsync(body)
      window.open(result.pdf_url, '_blank')
    } catch {
      toast.error('BOL generation failed.')
    }
  }

  return (
    <div className="flex flex-col h-full overflow-auto">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border-default bg-surface sticky top-0 z-10">
        <div className="flex-1">
          <h1 className="text-lg font-semibold">Bill of Lading</h1>
          <p className="text-xs text-secondary mt-0.5">
            Fill out the standard BOL and generate a PDF to send to the carrier.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={handleGenerate} loading={generateBol.isPending}>
          {generateBol.isPending ? (
            <Loader2 size={14} className="mr-1 animate-spin" />
          ) : (
            <FileText size={14} className="mr-1" />
          )}
          Generate PDF
        </Button>
      </div>

      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-6 space-y-6">
        <FieldGroup>
          <Field label="BOL Number">
            <Input value={form.bol_number} onChange={(e) => set('bol_number', e.target.value)} />
          </Field>
          <Field label="Date">
            <Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} />
          </Field>
          <Field label="Carrier Name">
            <Input value={form.carrier_name} onChange={(e) => set('carrier_name', e.target.value)} />
          </Field>
          <Field label="Trailer / Car Number">
            <Input value={form.trailer_number} onChange={(e) => set('trailer_number', e.target.value)} />
          </Field>
        </FieldGroup>

        <div>
          <SectionHeader label="Ship From (Shipper)" />
          <SectionBody>
            <PartyFields party={form.shipper} onChange={(p) => set('shipper', p)} referenceLabel="SID #" />
          </SectionBody>
        </div>

        <div>
          <SectionHeader label="Ship To (Consignee)" />
          <SectionBody>
            <PartyFields party={form.consignee} onChange={(p) => set('consignee', p)} referenceLabel="CID #" />
          </SectionBody>
        </div>

        <div>
          <SectionHeader label="Third Party Freight Charges Bill To" />
          <SectionBody>
            <Checkbox checked={thirdParty} onChange={(e) => setThirdParty(e.target.checked)} label="Applicable" />
            {thirdParty && (
              <PartyFields
                party={form.third_party_bill_to ?? blankParty()}
                onChange={(p) => set('third_party_bill_to', p)}
                referenceLabel="Reference"
              />
            )}
          </SectionBody>
        </div>

        <div>
          <SectionHeader label="Freight Charge Terms" />
          <SectionBody>
            <div className="flex items-center gap-6">
              {FREIGHT_TERMS_OPTIONS.map((opt) => (
                <Radio
                  key={opt.value}
                  name="freight_charge_terms"
                  checked={form.freight_charge_terms === opt.value}
                  onChange={() => set('freight_charge_terms', opt.value)}
                  label={opt.label}
                />
              ))}
            </div>
          </SectionBody>
        </div>

        <div>
          <SectionHeader label="Special Instructions" />
          <SectionBody>
            <Textarea
              value={form.special_instructions}
              onChange={(e) => set('special_instructions', e.target.value)}
              rows={3}
            />
          </SectionBody>
        </div>

        <div>
          <SectionHeader label="Customer Order Information" />
          <SectionBody>
            <CustomerOrderLinesEditor
              lines={form.customer_order_lines}
              onChange={(lines) => set('customer_order_lines', lines)}
            />
          </SectionBody>
        </div>

        <div>
          <SectionHeader label="Carrier Information" />
          <SectionBody>
            <CommodityLinesEditor
              lines={form.commodity_lines}
              onChange={(lines) => set('commodity_lines', lines)}
            />
          </SectionBody>
        </div>

        <FieldGroup>
          <Field label="Grand Total">
            <Input value={form.grand_total} onChange={(e) => set('grand_total', e.target.value)} placeholder="$0.00" />
          </Field>
          <Field label="COD Amount">
            <Input value={form.cod_amount} onChange={(e) => set('cod_amount', e.target.value)} placeholder="$0.00" />
          </Field>
          <Field label="COD Terms">
            <Select value={form.cod_terms} onChange={(e) => set('cod_terms', e.target.value as CodTerms)}>
              {COD_TERMS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </Field>
        </FieldGroup>

        <div>
          <SectionHeader label="Carrier Liability Limitation" />
          <SectionBody>
            <FieldGroup>
              <Field label="Declared Value (not exceeding)">
                <Input value={form.liability_value} onChange={(e) => set('liability_value', e.target.value)} placeholder="e.g. 5.00" />
              </Field>
              <Field label="Per Unit">
                <Input value={form.liability_unit} onChange={(e) => set('liability_unit', e.target.value)} placeholder="e.g. lb" />
              </Field>
            </FieldGroup>
          </SectionBody>
        </div>

        <div>
          <SectionHeader label="Signatures" />
          <SectionBody>
            <FieldGroup>
              <Field label="Shipper Printed Name">
                <Input value={form.shipper_printed_name} onChange={(e) => set('shipper_printed_name', e.target.value)} />
              </Field>
              <Field label="Shipper Date">
                <Input type="date" value={form.shipper_date} onChange={(e) => set('shipper_date', e.target.value)} />
              </Field>
            </FieldGroup>
            <FieldGroup>
              <Field label="Carrier Printed Name">
                <Input value={form.carrier_printed_name} onChange={(e) => set('carrier_printed_name', e.target.value)} />
              </Field>
              <Field label="Carrier Date">
                <Input type="date" value={form.carrier_date} onChange={(e) => set('carrier_date', e.target.value)} />
              </Field>
            </FieldGroup>
          </SectionBody>
        </div>
      </div>
    </div>
  )
}
